import React from "react";
import { Dropdown } from "new-react-bootstrap";

import SearchInput from "@stadium/ui/src/components/search-input/search-input";
import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";
import { Spinner } from "@stadium/ui/src/components/loaders/loaders";

import style from "./main.module.scss";

const CheckDropdownItem = ({
  children,
  id,
  identifier: key,
  checked,
  onChange,
  filterItemClass = "",
  childClassName = "",
}) => {
  let iconClassName = "iCheckbox";
  iconClassName += checked ? " iconCheckboxTick " : " iconCheckboxDefault ";
  let sortListClassName = "csCheckbox ";
  sortListClassName += checked ? "active " : "";
  return (
    <div className={`filterDropdownItem ${sortListClassName} ${filterItemClass}`}>
      <label className={`labelText ${checked ? "active " : ""}`} tabIndex="0">
        <input type="checkbox" checked={checked} onChange={() => onChange(key, id)} />
        <span className="csSpan ">
          <i className={iconClassName} />
          <span className={childClassName}>{children}</span>
        </span>
      </label>
    </div>
  );
};

export const renderSearchFilter = ({
  options,
  showSearchFilter,
  id,
  searchfilterOptions = {},
  name,
  onFilter,
}) => {
  if (showSearchFilter) {
    return (
      <SearchInput
        id={`${id}SearchFilter`}
        name={`${name}SearchFilter`}
        {...searchfilterOptions}
        data={options}
        onFilter={onFilter}
      />
    );
  }
};

const renderCheckboxItemContent = ({
  key,
  items,
  onChange,
  label,
  selectedIds,
  handleChecked,
  keyHeadingClass,
  filterItemClass,
  imageClassName = "",
  isNextImage = true,
  imgHeight = 60,
  imgWidth = 60,
  childClassName = "",
}) => (
  <>
    <div className={`keyHeading ${keyHeadingClass}`}>{label}</div>
    {items.map((i) => (
      <CheckDropdownItem
        key={i.id}
        id={i.id}
        identifier={key}
        checked={selectedIds.includes(i.id)}
        onChange={(key, id) => handleChecked(key, id, onChange)}
        filterItemClass={filterItemClass}
        childClassName={childClassName}
      >
        {i?.imageSrc && (
          <Multimedia
            type="image"
            className={imageClassName}
            isNextImage={isNextImage}
            src={i.imageSrc}
            height={imgHeight}
            width={imgWidth}
          />
        )}
        {i.name}
      </CheckDropdownItem>
    ))}
  </>
);

const renderRadioButtonContent = ({
  key,
  items,
  onChange,
  label,
  selectedIds,
  keyHeadingClass,
  isRadioContent = true,
  customClass = "",
  showSearchFilter = false,
  onFilter,
  searchData,
  searchfilterOptions,
  includeNoResultForSearch,
  itemsListBlock,
}) => (
  <>
    <div className={`keyHeading ${keyHeadingClass}`}>{label}</div>
    {renderSearchFilter({
      showSearchFilter,
      onFilter,
      id: key,
      name: key,
      options: searchData,
      searchfilterOptions,
    })}
    {includeNoResultForSearch ? (
      <div className="noResultOption">No matching results found</div>
    ) : (
      <div className={itemsListBlock}>
        {items.map((i) => {
          const { id, key: itemKey = id, label: itemLabel } = i;
          const activeOptionClass = selectedIds.includes(itemKey) ? "active" : "";

          return (
            <Dropdown.Item
              {...(Dropdown.Item.defaultProps || {})}
              id={itemKey}
              key={`radio-${label}-${itemKey}`}
              active={selectedIds.includes(itemKey)}
              name={key}
              value={itemKey}
              classes={`${style.customRadio} ${style.filterItemClass}`}
              onSelect={() => onChange({ key, id: itemKey })}
            >
              <label
                htmlFor={itemKey}
                className={isRadioContent ? "labelText" : `labelText ${activeOptionClass}`}
              >
                {isRadioContent ? (
                  <>
                    <i className={`radio-button-circle ${style.radioButtonCircle}`} />
                    <span className="helper-text">{itemLabel}</span>
                  </>
                ) : (
                  <div className={customClass}>{itemLabel}</div>
                )}
              </label>
            </Dropdown.Item>
          );
        })}
      </div>
    )}
  </>
);

export const CheckboxItem = ({ filterKey: key, label, isListRequired = true, ...rest }) => {
  const inputProps = {
    label,
    key,
    ...rest,
  };
  return isListRequired ? (
    <li key={key} className={`list-${label.toLowerCase()}`}>
      {renderCheckboxItemContent({ ...inputProps })}
    </li>
  ) : (
    renderCheckboxItemContent({ ...inputProps })
  );
};

export const RadioButtonItem = ({ filterKey: key, label, isListRequired = true, ...rest }) => {
  const inputProps = {
    label,
    key,
    ...rest,
  };

  return isListRequired ? (
    <li key={key} className={`list-${label.toLowerCase()} ${rest.wrapperClass}`}>
      {renderRadioButtonContent({ ...inputProps })}
    </li>
  ) : (
    renderRadioButtonContent({ ...inputProps })
  );
};

const CheckboxDropdown = ({
  id,
  parentLabel,
  label,
  data,
  className,
  dropDownClass = "",
  menuWrapper = "",
  keyHeadingClass = "",
  filterListWrapperClass = "",
  filterListClass = "",
  filterItemClass = "",
  childClassName = "",
  disabled = false,
  children,
  isParentLabelRequired = true,
  isDropDownOpen,
  onClick = null,
  customeButtonClass = "",
  iconClassName = "",
  renderSearchInput,
  itemsListBlock,
  onToggle,
}) => {
  const handleChecked = (key, id, onChange) => {
    onChange({ key, id });
  };

  const handleToggle = (open) => onToggle?.(open);

  const renderMenuItem = ({
    key,
    items,
    onChange,
    label,
    selectedIds,
    type = "checkbox",
    customClass = "",
    isRadioContent = true,
    showSearchFilter = false,
    searchfilterOptions = {},
    onFilter,
    searchData = [],
    includeNoResultForSearch = false,
    loader,
    wrapperClass,
  }) => {
    if (loader?.display) {
      return (
        <li key={key} className={style.dropdownLanguageBlock}>
          <div className={style.loadingLanguageSpinner}>
            <Spinner />
          </div>
          <div className={style.loadingLanguageText}>{loader?.text}</div>
        </li>
      );
    }
    const inputParams = {
      filterKey: key,
      items,
      onChange,
      label,
      selectedIds,
      handleChecked,
      keyHeadingClass,
      filterItemClass,
      childClassName,
      isRadioContent,
      customClass,
      showSearchFilter,
      onFilter,
      searchData,
      searchfilterOptions,
      itemsListBlock,
      wrapperClass,
      includeNoResultForSearch: includeNoResultForSearch && items?.length === 0,
    };
    if (type === "checkbox") return <CheckboxItem key={key} {...inputParams} />;
    else if (type === "radioButton") return <RadioButtonItem key={key} {...inputParams} />;
    return null;
  };

  return (
    <Dropdown
      {...(Dropdown.defaultProps || {})}
      className={className}
      bsPrefix="multiSelectDropdown bsMultiSelectDropdown"
      id={id}
      show={isDropDownOpen}
      onToggle={handleToggle}
    >
      <Dropdown.Toggle
        {...(Dropdown.Toggle.defaultProps || {})}
        variant="outline"
        id="dropdown-basic"
        bsPrefix={`ftDropdownToggle ${style.ftDropdownToggle} ${customeButtonClass}`}
        disabled={disabled}
      >
        <span className="text">{label}</span>
        <i className={`iconChevronDown ${iconClassName}`} />
      </Dropdown.Toggle>
      <div className="filterDropOverlay"></div>
      <Dropdown.Menu
        {...(Dropdown.Menu.defaultProps || {})}
        bsPrefix={`filterDropdownMenu ${style.displayDropdown} ${dropDownClass}`}
      >
        <article className={`filterDropdownMenuWrapper ${menuWrapper}`}>
          {isParentLabelRequired && <span className="labelMobile">{parentLabel || label}</span>}
          <i className="iconClose" onClick={onClick} />
          <div className={filterListWrapperClass}>
            <div className={`filterListWrap ${filterListClass}`}>
              {renderSearchInput?.()}
              {data.map(renderMenuItem)}
              {children}
            </div>
          </div>
        </article>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CheckboxDropdown;
